/**
 * Metadatos de las métricas que el payload de Overview no trae: **dirección**
 * (si crecer está bien) y **dueño**.
 *
 * `MetricValue` no los incluye a propósito — son de la definición del KPI, que
 * es un registro aparte — y sin ellos dos cosas se rompen: el delta queda en
 * gris (un número solo no sabe si subir es bueno) y el frente crítico no puede
 * decir de quién es.
 *
 * Sale del catálogo (`GET /api/metrics`), que ya viene recortado por rol: ops y
 * comercial no reciben las definiciones de las métricas de plata. Es una consulta
 * **no bloqueante**: si falla, la pantalla se dibuja igual con el delta en
 * neutro y el frente sin dueño, que es lo honesto.
 */
import type { MetricDefinition, MetricValue, UserRef } from '@ops/contracts'
import { useMemo } from 'react'

import { useMetrics, useUsers } from '../../lib/queries'

export interface MetricMeta {
  /** La definición completa del KPI, para el drawer de detalle. */
  defDe: (slug: string) => MetricDefinition | undefined
  /**
   * Valor del KPI en el período, del catálogo. La fila héroe del Overview trae
   * cinco métricas y las alertas de la API hablan de veintiuna, así que sin esto
   * la deuda de datos de las otras dieciséis no tiene con qué dibujarse: ni
   * nombre, ni proveniencia, ni unidad para el diálogo de carga.
   *
   * `undefined` = el catálogo todavía no llegó o falló. La consulta es no
   * bloqueante, así que quien la use tiene que poder dibujarse sin ella.
   */
  valorDe: (slug: string) => MetricValue | undefined
  /** `undefined` = no la sabemos: el delta se muestra en neutro. */
  direccionDe: (slug: string) => 'higher_better' | 'lower_better' | undefined
  /** Dueño de la métrica. `null` = sin dueño asignado, y se dice así. */
  ownerDe: (slug: string) => UserRef | null
  /** Directorio de personas asignables, para el selector de dueño. */
  usuarios: readonly UserRef[]
}

export function useMetricMeta(periodo: string): MetricMeta {
  const { data: catalogo } = useMetrics(periodo)
  const { data: usuarios } = useUsers()

  return useMemo(() => {
    const definiciones = new Map<string, MetricDefinition>(
      (catalogo?.metricas ?? []).map((d) => [d.slug, d]),
    )
    const personas = new Map<string, UserRef>(
      (usuarios ?? []).map((u) => [u.id, u]),
    )
    const valores = new Map<string, MetricValue>(
      (catalogo?.valores ?? []).map((v) => [v.slug, v]),
    )

    return {
      defDe: (slug) => definiciones.get(slug),
      valorDe: (slug) => valores.get(slug),
      direccionDe: (slug) => definiciones.get(slug)?.direccion,
      ownerDe: (slug) => {
        const ownerId = definiciones.get(slug)?.ownerId
        if (ownerId === undefined || ownerId === null) return null
        return personas.get(ownerId) ?? null
      },
      usuarios: usuarios ?? [],
    }
  }, [catalogo, usuarios])
}
